import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/Modal';
import { apiClient } from '@/services/api';
import type { InventoryItem } from '@/types/api';

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: number;
  item: InventoryItem | null;
}

type AdjustmentType = 'IN' | 'OUT' | 'ADJUSTMENT';

const adjustmentTypeOptions = [
  { value: 'IN', label: 'Stock In/Add' },
  { value: 'OUT', label: 'Stock Out/Remove' },
  { value: 'ADJUSTMENT', label: 'Correction' },
];

const reasonOptions = [
  { value: 'Delivery', label: 'Delivery' },
  { value: 'Sale', label: 'Sale' },
  { value: 'Waste/Spoilage', label: 'Waste/Spoilage' },
  { value: 'Theft/Loss', label: 'Theft/Loss' },
  { value: 'Return to Supplier', label: 'Return to Supplier' },
  { value: 'Inventory Count Correction', label: 'Inventory Count Correction' },
  { value: 'Transfer', label: 'Transfer' },
  { value: 'Other', label: 'Other' },
];

export function StockAdjustmentModal({
  isOpen,
  onClose,
  restaurantId,
  item,
}: StockAdjustmentModalProps) {
  const queryClient = useQueryClient();
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('IN');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const adjustStockMutation = useMutation({
    mutationFn: (data: {
      quantity: number;
      type: AdjustmentType;
      reason: string;
      reference?: string;
    }) => {
      if (!item) {
        throw new Error('No item selected');
      }
      return apiClient.adjustInventoryStock(restaurantId, item.id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts', restaurantId] });
      handleClose();
    },
  });

  const handleClose = () => {
    setAdjustmentType('IN');
    setQuantity('');
    setReason('');
    setReference('');
    setErrors({});
    onClose();
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!quantity || parseInt(quantity) < 1) {
      newErrors.quantity = 'Quantity must be at least 1';
    }

    if (!reason) {
      newErrors.reason = 'Reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    adjustStockMutation.mutate({
      quantity: parseInt(quantity),
      type: adjustmentType,
      reason,
      reference: reference || undefined,
    });
  };

  if (!item) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <form onSubmit={handleSubmit}>
        <ModalHeader onClose={handleClose}>
          Adjust Stock - {item.name}
        </ModalHeader>

        <ModalBody>
          <div className="space-y-4">
            <div className="bg-gray-100 border border-gray-300 rounded-md p-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Current Stock:</span>
                  <span className="ml-2 text-gray-900">{item.quantity} {item.unit}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">SKU:</span>
                  <span className="ml-2 text-gray-900">{item.sku}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Min Threshold:</span>
                  <span className="ml-2 text-gray-900">{item.minThreshold} {item.unit}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Max Threshold:</span>
                  <span className="ml-2 text-gray-900">{item.maxThreshold} {item.unit}</span>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="adjustmentType" className="block text-sm font-medium text-gray-700 mb-1">
                Adjustment Type <span className="text-red-500">*</span>
              </label>
              <select
                id="adjustmentType"
                value={adjustmentType}
                onChange={(e) => setAdjustmentType(e.target.value as AdjustmentType)}
                className="input w-full"
              >
                {adjustmentTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
                Quantity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="quantity"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className={`input w-full ${errors.quantity ? 'border-red-500' : ''}`}
                placeholder="Enter quantity"
              />
              {errors.quantity && (
                <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
              )}
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={`input w-full ${errors.reason ? 'border-red-500' : ''}`}
              >
                <option value="">Select a reason...</option>
                {reasonOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.reason && (
                <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
              )}
            </div>

            <div>
              <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1">
                Reference
              </label>
              <input
                type="text"
                id="reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="input w-full"
                placeholder="e.g., PO#12345, Invoice#..."
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-outline"
            disabled={adjustStockMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={adjustStockMutation.isPending}
          >
            {adjustStockMutation.isPending ? 'Applying...' : 'Apply Adjustment'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
